/* eslint-disable @next/next/no-img-element */
"use client";

import type { CSSProperties } from "react";

import type { IInternshipCourse } from "@/api/internshipData";

/**
 * One catalogue card. Ported from the source CourseCard; breakpoints translated
 * per `@/widgets/internship/container` (source sm 640 → `min-[640px]:`).
 *
 * Every colour is optional. An EMPTY value keeps the built-in design — the
 * Tailwind class or crimson constant the source used — rather than falling back
 * to a hex copy of it, so defaults render exactly as before and only an explicit
 * CMS value overrides. `preparePreviewData` passes values through untouched, so
 * the admin preview and the live page resolve identically.
 *
 * ── Reproducing the source's shadcn primitives ──────────────────────────────
 * The source composes `<Card>`, `<Badge>` and `<Button>`, whose base classes
 * survive `twMerge` and are therefore part of how the card actually renders.
 * This project has no shadcn primitives, so the SURVIVING classes are written
 * out here:
 *
 *   Card   → `rounded-xl border bg-white text-slate-950` (its `shadow` and
 *            `border-slate-200` lose to the call site's `shadow-md` /
 *            `border-gray-100`).
 *   Badge  → `inline-flex items-center border border-transparent text-xs shadow
 *            transition-colors` (the call site overrides only bg/text/radius/
 *            padding/weight). NOTE `text-xs`, not `text-sm`.
 *   Button → `inline-flex items-center justify-center whitespace-nowrap
 *            transition-colors` + size `default` = `h-7 px-5`. `h-7` is what
 *            actually sets the height — the call site's `py-2.5` is inert
 *            against a fixed height — so both buttons are 28px tall.
 *
 * Two Tailwind-token differences between the projects are also pinned:
 *   - `bg-white` is #f8f9fa here (globals.css `@theme --color-white`), so pure
 *     white is written as a literal.
 *   - The source's `text-sm`/`text-xs`/shadow scale is Tailwind v3. This project
 *     is v4, where v3 `shadow` = `shadow-sm` and v3 `shadow-sm` = `shadow-xs`.
 *
 * The source wraps the title in a shadcn Tooltip. This project has no shadcn
 * primitives, so the full name is carried on `title` instead — same purpose
 * (reading a truncated name), no new dependency.
 */
export interface CourseCardTheme {
  cardBackgroundColor?: string;
  cardBorderColor?: string;
  cardTitleColor?: string;
  badgeBackgroundColor?: string;
  badgeTextColor?: string;
  detailBackgroundColor?: string;
  detailTextColor?: string;
  applyLabel?: string;
  applyBackgroundColor?: string;
  applyHoverBackgroundColor?: string;
  applyTextColor?: string;
  /**
   * Download Brochure button. Styling only — the button is drawn ONLY when
   * `course.brochure_url` is present, so none of these can make it appear on a
   * course the API returned without a brochure.
   */
  brochureLabel?: string;
  brochureBackgroundColor?: string;
  brochureHoverBackgroundColor?: string;
  brochureTextColor?: string;
  brochureBorderColor?: string;
  brochureIcon?: string;
}

interface CourseCardProps {
  course: IInternshipCourse;
  theme?: CourseCardTheme;
  /** Undefined renders the Apply button disabled (the modal is not built yet). */
  onApply?: (course: IInternshipCourse) => void;
}

/** Design defaults, used whenever the CMS leaves a colour blank. */
const CRIMSON = "#9F1C33";
const CRIMSON_HOVER = "#8A1829";
const DEFAULT_APPLY_LABEL = "Apply Now";
/**
 * Brochure button defaults, matching the source's `bg-pink-100` /
 * `border-pink-200` / `hover:bg-pink-200`. Written as literals because the
 * source project remaps `pink-100` to #FCE7F6, and this project is on Tailwind
 * v4 whose oklch pinks differ slightly from the v3 values the source rendered.
 */
const BROCHURE_BG = "#FCE7F6";
const BROCHURE_HOVER_BG = "#FBCFE8";
const BROCHURE_BORDER = "#FBCFE8";
const DEFAULT_BROCHURE_LABEL = "Download Brochure";

/** Only emit a CSS property when the editor actually set a value. */
const styleIf = (
  property: keyof CSSProperties,
  value?: string
): CSSProperties => (value?.trim() ? { [property]: value.trim() } : {});

/** "₹ 3,284/-" — Indian digit grouping, matching the source. */
const formatFee = (fee?: number): string =>
  typeof fee === "number" && Number.isFinite(fee)
    ? `₹ ${Number(fee).toLocaleString("en-IN")}/-`
    : "";

export default function CourseCard({
  course,
  theme = {},
  onApply,
}: CourseCardProps) {
  const title = course.course_name?.trim() || "";
  const image = course.image_url?.trim() || "";
  const badge = course.category?.type?.trim() || "";
  const brochureUrl = course.brochure_url?.trim() || "";
  const fee = formatFee(course.fee_amount);

  const applyBg = theme.applyBackgroundColor?.trim() || CRIMSON;
  const applyHoverBg = theme.applyHoverBackgroundColor?.trim() || CRIMSON_HOVER;
  const applyLabel = theme.applyLabel?.trim() || DEFAULT_APPLY_LABEL;

  const brochureBg = theme.brochureBackgroundColor?.trim() || BROCHURE_BG;
  const brochureHoverBg =
    theme.brochureHoverBackgroundColor?.trim() || BROCHURE_HOVER_BG;
  const brochureLabel = theme.brochureLabel?.trim() || DEFAULT_BROCHURE_LABEL;
  const brochureIcon = theme.brochureIcon?.trim() || "";

  /** Same client-side download the source performs — a synthetic `<a download>`. */
  const handleDownloadBrochure = () => {
    const link = document.createElement("a");
    link.href = brochureUrl;
    link.download = `${title.replace(/\s+/g, "-").toLowerCase()}-brochure.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const rows: Array<[string, string]> = [];
  // The source writes a space before each colon ("Mode :"); kept verbatim.
  if (course.mode?.trim()) rows.push(["Mode :", course.mode.trim()]);
  if (course.duration?.trim()) rows.push(["Duration :", course.duration.trim()]);
  if (fee) rows.push(["Fee :", fee]);

  return (
    <div
      className="p-3 border border-gray-100 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow h-full flex flex-col bg-[#FFFFFF] text-slate-950"
      style={{
        ...styleIf("backgroundColor", theme.cardBackgroundColor),
        ...styleIf("borderColor", theme.cardBorderColor),
      }}
    >
      <div className="relative h-44 overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          <div className="w-full h-full rounded bg-gray-100" />
        )}
        {badge ? (
          // hover colour pinned so the badge doesn't shift on card hover
          <span
            className="absolute top-4 right-4 inline-flex items-center border border-transparent bg-[#FCD34D] hover:bg-[#FCD34D] text-black px-3 py-1 rounded-full font-medium text-xs shadow-sm transition-colors"
            style={{
              ...styleIf("backgroundColor", theme.badgeBackgroundColor),
              ...styleIf("color", theme.badgeTextColor),
            }}
          >
            {badge}
          </span>
        ) : null}
      </div>

      {/* The source's card body wrapper — `flex-1` so short cards still push the
          buttons to the bottom via `mt-auto`. */}
      <div className="flex-1 flex flex-col">
        <h3
          className="text-lg font-semibold my-5 text-gray-900 tab:truncate break-words whitespace-normal"
          style={styleIf("color", theme.cardTitleColor)}
          title={title}
        >
          {title}
        </h3>

        {rows.length ? (
          <div
            className="space-y-2 mb-5 bg-[#FFF5F5] p-3 rounded-lg"
            style={{
              ...styleIf("backgroundColor", theme.detailBackgroundColor),
              ...styleIf("color", theme.detailTextColor),
            }}
          >
            {rows.map(([label, value]) => (
              <div key={label} className="flex items-start">
                <span className="text-sm font-bold mr-1">{label}</span>
                <span
                  className={`text-sm${
                    label === "Mode :" ? " capitalize" : ""
                  }`}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {/* Pinned to the bottom so cards in a row line up. */}
        <div className="mt-auto space-y-3">
          <button
            type="button"
            disabled={!onApply}
            onClick={onApply ? () => onApply(course) : undefined}
            style={{
              backgroundColor: applyBg,
              color: theme.applyTextColor?.trim() || "#FFFFFF",
            }}
            onMouseEnter={(e) => {
              if (onApply) e.currentTarget.style.backgroundColor = applyHoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = applyBg;
            }}
            className="inline-flex items-center justify-center whitespace-nowrap w-full h-7 px-5 py-2.5 text-sm font-medium rounded-md shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {applyLabel}
          </button>

          {/* As in the source, this exists ONLY for courses whose API row
              carries a brochure_url. The CMS brochure_button_* fields style it;
              they cannot make it appear on a course without one. */}
          {brochureUrl ? (
            <button
              type="button"
              onClick={handleDownloadBrochure}
              style={{
                backgroundColor: brochureBg,
                borderColor: theme.brochureBorderColor?.trim() || BROCHURE_BORDER,
                color: theme.brochureTextColor?.trim() || CRIMSON,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = brochureHoverBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = brochureBg;
              }}
              className="inline-flex items-center justify-center whitespace-nowrap w-full h-7 px-5 py-2.5 text-xs font-medium rounded-md border shadow-xs transition-colors"
            >
              {brochureIcon ? (
                <img
                  src={brochureIcon}
                  alt=""
                  aria-hidden="true"
                  className="mr-2 h-4 w-4 object-contain"
                />
              ) : (
                /* lucide Download, inlined (the source uses <Download className="mr-2 h-4 w-4" />) */
                <svg
                  className="mr-2 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" x2="12" y1="15" y2="3" />
                </svg>
              )}
              {brochureLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
